import { formatDate, subtractDate } from "./dates";

export type PieChartDatum = {
  name: string;
  value: number;
};

const toPieSliceName = (
  key: string,
  sliceLabels?: Record<string, string>
): string => {
  return sliceLabels?.[key] ?? `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
};

export const toPieChartData = (
  datum: object,
  sliceLabels?: Record<string, string>
): PieChartDatum[] => {
  return Object.entries(datum)
    .filter((entry): entry is [string, number] => typeof entry[1] === "number")
    .map(([key, value]) => ({
      name: toPieSliceName(key, sliceLabels),
      value,
    }));
};

export const pieSeriesHasValues = (datum: unknown): boolean => {
  if (!datum || typeof datum !== "object") {
    return false;
  }

  return Object.values(datum).some(
    (value) => typeof value === "number" && value > 0
  );
};

export const prependZeroDataPoint = <T extends Record<string, string | number>>(
  data: T[],
  dateField: keyof T
): T[] => {
  if (data.length === 0) {
    return data;
  }

  const firstDataPoint = data[0];
  const firstDate = firstDataPoint[dateField] as string;

  // Get date one month prior to the first data point
  const zeroDate = formatDate(
    subtractDate(firstDate, 1, "month"),
    "YYYY-MM-DD"
  );

  // Create zero data point with all fields set to 0 except the date field
  const zeroDataPoint = { ...firstDataPoint };

  // Set all numeric fields to 0, but keep the date field as the calculated zero date
  Object.keys(zeroDataPoint).forEach((key) => {
    if (
      key !== dateField &&
      typeof zeroDataPoint[key as keyof T] === "number"
    ) {
      (zeroDataPoint as Record<string, string | number>)[key] = 0;
    }
  });

  // Set the date field to the zero date
  (zeroDataPoint as Record<string, string | number>)[dateField as string] =
    zeroDate;

  return [zeroDataPoint, ...data];
};
